import { useState, useEffect } from 'react'
import invitationsService from '../services/invitations'
import usersService from '../services/users'
import Card from '../components/common/Card'
import Input from '../components/common/Input'
import Button from '../components/common/Button'
import Alert from '../components/common/Alert'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { useAuth } from '../contexts/AuthContext'

const InvitationsPage = () => {
  const { user } = useAuth()
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Form state
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('standard')
  const [creating, setCreating] = useState(false)
  const [generatedUrl, setGeneratedUrl] = useState('')

  // Revoke state
  const [revokingId, setRevokingId] = useState(null)
  const [confirmRevokeId, setConfirmRevokeId] = useState(null)

  // Users state
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(true)
  const [disablingUserId, setDisablingUserId] = useState(null)
  const [confirmDisableUserId, setConfirmDisableUserId] = useState(null)
  const [promotingUserId, setPromotingUserId] = useState(null)
  const [confirmPromoteUserId, setConfirmPromoteUserId] = useState(null)
  const [demotingUserId, setDemotingUserId] = useState(null)
  const [confirmDemoteUserId, setConfirmDemoteUserId] = useState(null)

  // Load invitations and users on mount
  useEffect(() => {
    loadInvitations()
    loadUsers()
  }, [])

  const loadInvitations = async () => {
    try {
      setLoading(true)
      const data = await invitationsService.listInvitations()
      // Handle both array response and paginated response
      const invitationsList = Array.isArray(data) ? data : data.results || []
      setInvitations(invitationsList)
      setError('')
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load invitations')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateInvitation = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setGeneratedUrl('')
    setCreating(true)

    try {
      const invitation = await invitationsService.createInvitation(email, role)

      // Generate the invitation URL
      const baseUrl = window.location.origin
      const inviteUrl = `${baseUrl}/invitation/${invitation.token}`
      setGeneratedUrl(inviteUrl)

      // Clear form and reload list
      setEmail('')
      setRole('standard')
      setSuccess(`Invitation created for ${invitation.email}`)
      await loadInvitations()
    } catch (err) {
      setError(
        err.response?.data?.detail || err.response?.data?.role?.[0] || 'Failed to create invitation'
      )
    } finally {
      setCreating(false)
    }
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedUrl)
    setSuccess('Invitation URL copied to clipboard!')
  }

  const handleRevokeClick = (invitationId) => {
    setConfirmRevokeId(invitationId)
  }

  const handleCancelRevoke = () => {
    setConfirmRevokeId(null)
  }

  const handleConfirmRevoke = async (invitationId) => {
    setError('')
    setSuccess('')
    setRevokingId(invitationId)
    setConfirmRevokeId(null)

    try {
      await invitationsService.revokeInvitation(invitationId)
      setSuccess('Invitation revoked successfully')
      await loadInvitations()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke invitation')
    } finally {
      setRevokingId(null)
    }
  }

  const loadUsers = async () => {
    try {
      setUsersLoading(true)
      const data = await usersService.listUsers()
      // Handle both array response and paginated response
      const usersList = Array.isArray(data) ? data : data.results || []
      setUsers(usersList)
    } catch (err) {
      console.error('Failed to load users:', err)
    } finally {
      setUsersLoading(false)
    }
  }

  const handleDisableUserClick = (userId) => {
    setConfirmDisableUserId(userId)
  }

  const handleCancelDisableUser = () => {
    setConfirmDisableUserId(null)
  }

  const handleConfirmDisableUser = async (userId) => {
    setError('')
    setSuccess('')
    setDisablingUserId(userId)
    setConfirmDisableUserId(null)

    try {
      const result = await usersService.disableUser(userId)
      setSuccess(result.message)
      await loadUsers()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable user')
    } finally {
      setDisablingUserId(null)
    }
  }

  const handlePromoteUserClick = (userId) => {
    setConfirmPromoteUserId(userId)
  }

  const handleCancelPromoteUser = () => {
    setConfirmPromoteUserId(null)
  }

  const handleConfirmPromoteUser = async (userId) => {
    setError('')
    setSuccess('')
    setPromotingUserId(userId)
    setConfirmPromoteUserId(null)

    try {
      const result = await usersService.promoteUser(userId)
      setSuccess(result.message)
      await loadUsers()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to promote user')
    } finally {
      setPromotingUserId(null)
    }
  }

  const handleDemoteUserClick = (userId) => {
    setConfirmDemoteUserId(userId)
  }

  const handleCancelDemoteUser = () => {
    setConfirmDemoteUserId(null)
  }

  const handleConfirmDemoteUser = async (userId) => {
    setError('')
    setSuccess('')
    setDemotingUserId(userId)
    setConfirmDemoteUserId(null)

    try {
      const result = await usersService.demoteUser(userId)
      setSuccess(result.message)
      await loadUsers()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to demote user')
    } finally {
      setDemotingUserId(null)
    }
  }

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) {
      return 'Never'
    }
    const date = new Date(lastLogin)
    return date.toLocaleString()
  }

  const getStatusBadge = (invitation) => {
    const now = new Date()
    const expiresAt = new Date(invitation.expires_at)

    if (invitation.is_used) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Used
        </span>
      )
    } else if (invitation.is_invalidated) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Revoked
        </span>
      )
    } else if (expiresAt < now) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Expired
        </span>
      )
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Pending
        </span>
      )
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString()
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Invitations</h2>
        <p className="text-gray-600 mt-2">Manage user invitations</p>
      </div>

      {/* Create Invitation Form */}
      <Card>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Create New Invitation</h3>

        {error && <Alert type="error" message={error} onClose={() => setError('')} />}
        {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

        <form onSubmit={handleCreateInvitation} className="space-y-4">
          <Input
            label="Email Address"
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            required
          />

          {/* Role dropdown - shown to admins and superusers */}
          {user?.is_staff && (
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                User Role
              </label>
              <select
                id="role"
                name="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="standard">Standard User</option>
                <option value="admin">Admin</option>
                {user?.is_superuser && <option value="superuser">Superuser</option>}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {role === 'standard' && 'Regular user with no administrative privileges'}
                {role === 'admin' && 'Can create invitations for standard and admin users'}
                {role === 'superuser' && 'Full system access - can create any type of invitation'}
              </p>
            </div>
          )}

          <Button type="submit" disabled={creating}>
            {creating ? 'Creating...' : 'Create Invitation'}
          </Button>
        </form>

        {/* Display generated URL */}
        {generatedUrl && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm font-medium text-green-900 mb-2">
              Invitation URL (copy and send to user):
            </p>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={generatedUrl}
                readOnly
                className="flex-1 px-3 py-2 bg-white border border-green-300 rounded text-sm font-mono"
              />
              <Button onClick={copyToClipboard} size="sm" variant="outline">
                Copy
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Invitations List */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900">All Invitations</h3>
          <Button onClick={loadInvitations} size="sm" variant="outline">
            Refresh
          </Button>
        </div>

        {loading ? (
          <LoadingSpinner className="py-8" />
        ) : invitations.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No invitations yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Used At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitations.map((invitation) => {
                  const isPending =
                    !invitation.is_used &&
                    !invitation.is_invalidated &&
                    new Date(invitation.expires_at) > new Date()
                  const isConfirming = confirmRevokeId === invitation.id
                  const isRevoking = revokingId === invitation.id

                  return (
                    <tr key={invitation.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {invitation.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {invitation.role_display || 'Standard User'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getStatusBadge(invitation)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(invitation.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invitation.used_at ? formatDate(invitation.used_at) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invitation.created_by_email || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {isPending && !isConfirming && !isRevoking && (
                          <button
                            onClick={() => handleRevokeClick(invitation.id)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            Cancel
                          </button>
                        )}
                        {isConfirming && (
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-700 text-xs">Confirm?</span>
                            <button
                              onClick={() => handleConfirmRevoke(invitation.id)}
                              className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              Yes
                            </button>
                            <button
                              onClick={handleCancelRevoke}
                              className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                            >
                              No
                            </button>
                          </div>
                        )}
                        {isRevoking && <span className="text-gray-500 text-xs">Revoking...</span>}
                        {!isPending && !isRevoking && '-'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* All Users Section */}
      <Card className="mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">All Users</h2>
          <Button onClick={loadUsers} size="sm" variant="outline">
            Refresh
          </Button>
        </div>

        {usersLoading ? (
          <LoadingSpinner />
        ) : users.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No active users</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((targetUser) => {
                  const isCurrentUser = targetUser.id === user.id
                  const isConfirmingDisable = confirmDisableUserId === targetUser.id
                  const isDisabling = disablingUserId === targetUser.id
                  const isConfirmingPromote = confirmPromoteUserId === targetUser.id
                  const isPromoting = promotingUserId === targetUser.id
                  const isConfirmingDemote = confirmDemoteUserId === targetUser.id
                  const isDemoting = demotingUserId === targetUser.id

                  // Determine user role
                  let userRole = 'Standard'
                  if (targetUser.is_superuser) {
                    userRole = 'Superuser'
                  } else if (targetUser.is_staff) {
                    userRole = 'Admin'
                  }

                  // Can disable: anyone except yourself and superusers
                  const canDisable = !isCurrentUser && !targetUser.is_superuser

                  // Can promote: standard users only
                  const canPromote = !targetUser.is_staff && !targetUser.is_superuser

                  // Can demote: admins only (not yourself, not superusers)
                  const canDemote =
                    !isCurrentUser && targetUser.is_staff && !targetUser.is_superuser

                  return (
                    <tr key={targetUser.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {targetUser.email}
                        {isCurrentUser && <span className="ml-2 text-xs text-gray-500">(You)</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {targetUser.full_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            targetUser.is_superuser
                              ? 'bg-purple-100 text-purple-800'
                              : targetUser.is_staff
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {userRole}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatLastLogin(targetUser.last_login)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          {/* Promote button */}
                          {canPromote && !isConfirmingPromote && !isPromoting && (
                            <button
                              onClick={() => handlePromoteUserClick(targetUser.id)}
                              className="text-blue-600 hover:text-blue-900 font-medium"
                            >
                              Promote
                            </button>
                          )}
                          {isConfirmingPromote && (
                            <div className="flex items-center space-x-1">
                              <span className="text-gray-700 text-xs">Promote?</span>
                              <button
                                onClick={() => handleConfirmPromoteUser(targetUser.id)}
                                className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                Yes
                              </button>
                              <button
                                onClick={handleCancelPromoteUser}
                                className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                              >
                                No
                              </button>
                            </div>
                          )}
                          {isPromoting && (
                            <span className="text-gray-500 text-xs">Promoting...</span>
                          )}

                          {/* Demote button */}
                          {canDemote && !isConfirmingDemote && !isDemoting && (
                            <button
                              onClick={() => handleDemoteUserClick(targetUser.id)}
                              className="text-orange-600 hover:text-orange-900 font-medium"
                            >
                              Demote
                            </button>
                          )}
                          {isConfirmingDemote && (
                            <div className="flex items-center space-x-1">
                              <span className="text-gray-700 text-xs">Demote?</span>
                              <button
                                onClick={() => handleConfirmDemoteUser(targetUser.id)}
                                className="px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700"
                              >
                                Yes
                              </button>
                              <button
                                onClick={handleCancelDemoteUser}
                                className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                              >
                                No
                              </button>
                            </div>
                          )}
                          {isDemoting && <span className="text-gray-500 text-xs">Demoting...</span>}

                          {/* Disable button */}
                          {canDisable && !isConfirmingDisable && !isDisabling && (
                            <button
                              onClick={() => handleDisableUserClick(targetUser.id)}
                              className="text-red-600 hover:text-red-900 font-medium"
                            >
                              Disable
                            </button>
                          )}
                          {isConfirmingDisable && (
                            <div className="flex items-center space-x-1">
                              <span className="text-gray-700 text-xs">Disable?</span>
                              <button
                                onClick={() => handleConfirmDisableUser(targetUser.id)}
                                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                              >
                                Yes
                              </button>
                              <button
                                onClick={handleCancelDisableUser}
                                className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                              >
                                No
                              </button>
                            </div>
                          )}
                          {isDisabling && (
                            <span className="text-gray-500 text-xs">Disabling...</span>
                          )}

                          {/* Show dash if no actions available */}
                          {!canPromote && !canDemote && !canDisable && (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

export default InvitationsPage
